import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { api, apiPublic } from '@/plugins/axios.js'
import { vue } from '@/main.js'

/**
 * Retorna os dados por equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getDadosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await api.get(
      `/app/vacina-covid19-infantil/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os dados por microárea
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 * @param {string} microarea
 *
 * @returns {Array}
 */
export async function getDadosPorMicroarea(cod_ibge, nu_ine, microarea) {
  try {
    const { data } = await api.get(
      `/app/vacina-covid19-infantil/${cod_ibge}/equipe/${nu_ine}/microarea/${microarea}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Formata o array de dados com as doses
 *
 * @param {*} cadastros
 * @returns {Array}
 */
export function formatarCadastrosComDoses(cadastros) {
  return cadastros.map((item, index) => {
    const vacinasPendentes = JSON.parse(item.vacinas_pendentes)
    const vacinas = {
      covid19: JSON.parse(item.covid19) ?? [],
    }

    const vacinaConfig = [
      {
        nome: 'coresCovid19',
        vacina: 'covid19',
        id_mn: '99',
        doses: [
          { label: '1ª DOSE', id_ds: '1', idade: '6 meses' },
          { label: '2ª DOSE', id_ds: '2', idade: '7 meses' },
          { label: '3ª DOSE', id_ds: '3', idade: '9 meses' },
        ],
      },
    ]

    const resultadoVacinas = vacinaConfig.reduce((acc, config) => {
      acc[config.nome] = processarDoses(
        vacinas[config.vacina],
        config.id_mn,
        config.doses,
        vacinasPendentes
      )
      return acc
    }, {})

    return {
      id: index,
      ...item,
      ...resultadoVacinas,
    }
  })
}

/**
 * Processa as doses configuradas de uma vacina.
 * @param {Object} vacina Dados da vacina
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {Array} dosesConfig Configuração das doses
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Array} Lista de doses processadas
 */
function processarDoses(vacina, id_mn, dosesConfig, vacinasPendentes) {
  return dosesConfig.map(({ label, id_ds, idade }) => {
    const dose = encontrarDose(vacina, id_mn, id_ds, vacinasPendentes)

    return {
      dose: label,
      cor: dose.cor,
      tag: dose.tag || '',
      id_ds: dose.id_ds || '',
      id_mn: dose.id_mn || '',
      dt_plc: dose.tipo === 'aplicada' ? dose.dt_plc : idade,
      dt_plc_br: dose.tipo === 'aplicada' ? dose.dt_plc_br : idade,
      orig: dose.orig || '',
      lt: dose.lt || '',
    }
  })
}

/**
 * Localiza a dose aplicada ou pendente de uma vacina.
 * @param {Array} doses Lista de doses aplicadas
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {String|Number} id_ds Identificador da dose
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Object} Dados da dose encontrada ou padrão
 */
function encontrarDose(doses, id_mn, id_ds, vacinasPendentes) {
  const aplicada = doses.find((el) => el.id_ds === id_ds && el.id_mn === id_mn)
  if (aplicada) {
    return {
      tipo: 'aplicada',
      cor: 'green',
      ...aplicada,
      dt_plc_br: aplicada.dt_plc?.split('-').reverse().join('/') || '',
    }
  }

  const pendente = vacinasPendentes?.find(
    (el) => el.id_ds === id_ds && el.id_mn === id_mn && el.na == '0'
  )
  if (pendente) {
    return {
      tipo: 'pendente',
      cor: 'red',
      ...pendente,
      dt_plc_br: '',
      orig: pendente.orig || '',
      lt: pendente.lt || '',
    }
  }

  return {
    tipo: 'pendente',
    id_ds: id_ds,
    id_mn: id_mn,
    cor: 'white',
    orig: '',
    lt: '',
  }
}

/**
 * Filtra os registros classificados como vermelhos.
 * @param {Array} cadastros Lista de registros
 * @returns {Array}
 */
export function exibirVermelhos(cadastros) {
  const cadastrosFormatados = formatarCadastrosComDoses(cadastros)

  const camposCores = ['coresCovid19']

  return cadastrosFormatados.filter((item) =>
    camposCores.some(
      (campo) =>
        Array.isArray(item[campo]) &&
        item[campo].some((dose) => dose.cor === 'red')
    )
  )
}

/**
 * Filtra os registros classificados como verdes.
 * @param {Array} cadastros Lista de registros
 * @returns {Array}
 */
export function exibirVerdes(cadastros) {
  const cadastrosFormatados = formatarCadastrosComDoses(cadastros)

  const camposCores = ['coresCovid19']

  const coresPermitidas = new Set(['green', 'white'])

  return cadastrosFormatados.filter((item) =>
    camposCores.every((campo) => {
      const arr = item[campo]
      return (
        Array.isArray(arr) && arr.every((el) => coresPermitidas.has(el.cor))
      )
    })
  )
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 */
export function generatePdf(cadastros, selectEquipe, microarea) {
  const titlePdf = 'COVID-19 Infantil'
  const titlePdfSave = utils.methods.titlePdf(titlePdf, selectEquipe.no_equipe)
  const descriptionPdf = 'Crianças entre 6 meses e 4 anos'
  const headersPdf = [
    'NOME',
    'NASCIMENTO',
    'MÃE',
    'MA',
    'COVID-19',
    'DOCUMENTO',
  ]

  const doses = formatarCadastrosComDoses(cadastros)
  const dataPdf = doses.map((item, index) => {
    const documentos = []
    if (cadastros[index]?.nu_cpf)
      documentos.push(`CPF ${cadastros[index].nu_cpf}`)
    if (cadastros[index]?.nu_cns)
      documentos.push(`CNS ${cadastros[index].nu_cns}`)

    return {
      no_cidadao: item.no_cidadao,
      dt_nascimento: utils.methods.formatDate(item.dt_nascimento),
      no_mae: item.no_mae,
      ma: item.micro_area,
      covid19: item.coresCovid19
        .map((d) => `${d.dose} - ${d.dt_plc_br}`)
        .join('\n'),
      documento: documentos.join('\n'),
    }
  })

  const doc = new jsPDF(utils.methods.encryptionPdf())
  const table = utils.methods.createTablePdf(dataPdf)

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64Brasao) => {
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64Brasao,
        titlePdf,
        descriptionPdf
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          { label: 'Equipe', value: selectEquipe.no_equipe },
          { label: 'CNES', value: selectEquipe.nu_cnes },
          { label: 'INE', value: selectEquipe.ine_esf_eap },
          { label: 'Microárea', value: microarea },
        ],
        verticalPosition
      )

      doc.autoTable({
        head: [headersPdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 6,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 6,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: ({ row }) => {
          if (row.section === 'head') doc.setTextColor(0, 0, 0)
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY)
      doc.save(`${titlePdfSave}.pdf`)

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

export const dataFields = {
  Nome: 'no_cidadao',
  Nascimento: {
    field: 'dt_nascimento',
    callback: (value) => {
      return value !== null ? utils.methods.formatDate(value) : ''
    },
  },
  Mãe: 'no_mae',
  MA: 'micro_area',
}

export const filterIdadeOptions = [
  { label: '11 - 12 anos', value: '11 - 12 anos' },
  { label: '13 - 14 anos', value: '13 - 14 anos' },
]

export function handleFilterIdade(data, filter) {
  let filtrado = data.filter((item) => {
    if (filter === null) {
      return item
    }
    if (filter === '11 - 12 anos') {
      return Number(item.idade_anos) >= 11 && Number(item.idade_anos) <= 12
    } else if (filter === '13 - 14 anos') {
      return Number(item.idade_anos) >= 13 && Number(item.idade_anos) <= 14
    } else {
      return item
    }
  })
  return filtrado
}
